import { create } from 'zustand';
import { i18n } from '../i18n';
import { getContacts } from '../api/generated/contacts/contacts';
import { useToastStore } from './useToastStore';
import { showApiError } from '../errors';
import type {
  FacilityFlowCoreModulesCustomersDtosContactDto,
  FacilityFlowCoreModulesCustomersDtosCreateContactRequest,
  FacilityFlowCoreModulesCustomersDtosUpdateContactRequest,
  FacilityFlowCoreModulesCustomersDtosSearchContactsResponse,
  GetApiV1ContactsParams,
} from '../api/generated/models';

type ContactDto = FacilityFlowCoreModulesCustomersDtosContactDto;
type CreateContactRequest = FacilityFlowCoreModulesCustomersDtosCreateContactRequest;
type UpdateContactRequest = FacilityFlowCoreModulesCustomersDtosUpdateContactRequest;
type SearchContactsResponse = FacilityFlowCoreModulesCustomersDtosSearchContactsResponse;

const {
  getApiV1Contacts,
  getApiV1ContactsId,
  postApiV1Contacts,
  putApiV1ContactsId,
  deleteApiV1ContactsId,
} = getContacts();

const t = (key: string) => i18n.t(key, { ns: 'customers' }) as string;

// why: monotonically increasing request id so only the latest fetchContacts result is applied.
let fetchSequence = 0;

interface ContactState {
  contacts: ContactDto[];
  selectedContact: ContactDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  
  fetchContacts: (params?: GetApiV1ContactsParams) => Promise<void>;
  getContactById: (id: string) => Promise<ContactDto | null>;
  createContact: (contact: CreateContactRequest) => Promise<void>;
  updateContact: (id: string, contact: UpdateContactRequest) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
}

export const useContactStore = create<ContactState>((set) => ({
  contacts: [],
  selectedContact: null,
  loading: false,
  saving: false,
  error: null,
  totalCount: 0,
  page: 1,
  pageSize: 10,

  fetchContacts: async (params?: GetApiV1ContactsParams) => {
    // why: the contact picker refetches on every customer switch; without a sequence guard
    // a slow response for the previous customer could land last and replace the list.
    const sequence = ++fetchSequence;
    set({ loading: true, error: null });
    try {
      const response = await getApiV1Contacts(params) as unknown as SearchContactsResponse;
      if (sequence !== fetchSequence) return;
      set({
        contacts: response.items || [],
        totalCount: response.totalCount || 0,
        page: response.page || 1,
        pageSize: response.pageSize || 10,
        loading: false,
      });
    } catch (error) {
      if (sequence !== fetchSequence) return;
      console.error('[ContactStore] fetch failed', error);
      set({
        error: t('contacts.errors.fetchFailed'),
        loading: false,
      });
    }
  },

  getContactById: async (id: string) => {
    set({ loading: true, error: null });
    try {
      const contact = await getApiV1ContactsId(id) as unknown as ContactDto;
      set({ selectedContact: contact, loading: false });
      return contact;
    } catch (error) {
      console.error('[ContactStore] getById failed', error);
      set({ 
        error: t('contacts.errors.fetchFailed'),
        loading: false,
        selectedContact: null,
      });
      return null;
    }
  },

  createContact: async (contactData: CreateContactRequest) => {
    set({ saving: true, error: null });
    try {
      await postApiV1Contacts(contactData);
      set({ saving: false });
      
      useToastStore.getState().addToast(t('contacts.messages.created'), 'success');
    } catch (error) {
      console.error('[ContactStore] create failed', error);
      // why: surface the BE error code (e.g. customers.contacts.invalidData) instead of a
      // generic message — showApiError toasts the translated code and stores set an i18n key.
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  updateContact: async (id: string, contactData: UpdateContactRequest) => {
    set({ saving: true, error: null });
    try {
      await putApiV1ContactsId(id, contactData);
      set({ saving: false });
      
      useToastStore.getState().addToast(t('contacts.messages.updated'), 'success');
    } catch (error) {
      console.error('[ContactStore] update failed', error);
      // why: surface the BE error code (e.g. customers.contacts.invalidData) instead of a
      // generic message — showApiError toasts the translated code and stores set an i18n key.
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },

  deleteContact: async (id: string) => {
    set({ saving: true, error: null });
    try {
      await deleteApiV1ContactsId(id);
      set({ saving: false });
      
      useToastStore.getState().addToast(t('contacts.messages.deleted'), 'success');
    } catch (error) {
      console.error('[ContactStore] delete failed', error);
      // why: surface the BE error code (e.g. customers.contacts.invalidData) instead of a
      // generic message — showApiError toasts the translated code and stores set an i18n key.
      const apiError = showApiError(error);
      set({ error: apiError.code, saving: false });
      throw error;
    }
  },
}));
